/**
 * Shrink a picture to a thumbnail in the browser, before it is uploaded.
 *
 * The Ads page stores one small picture per ad (a data URL of at most ~100 KB, checked
 * again by the server). A screenshot of an ad is far larger, so it is drawn onto a canvas
 * at thumbnail size and re-encoded as JPEG, stepping the quality down until it fits.
 */

/** The server's limit (functions/analytics/adImages.js MAX_DATA_URL), with a little room. */
export const MAX_THUMB_CHARS = 130000;
export const THUMB_EDGE = 360;

/** The size that fits within `max` on the long edge, never enlarging. */
export function fitWithin(width, height, max = THUMB_EDGE) {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file could not be read as a picture.')); };
    img.src = url;
  });
}

/** A picture file → a JPEG data URL small enough to store. */
export async function fileToThumbnail(file, { max = THUMB_EDGE } = {}) {
  if (!file || !/^image\/(jpeg|png|webp)$/.test(file.type)) {
    throw new Error('Choose a JPEG, PNG or WebP picture.');
  }
  const img = await loadImage(file);
  const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, max);
  if (!width) throw new Error('That picture is empty.');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // JPEG has no transparency: a see-through PNG would otherwise come out black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  for (const quality of [0.85, 0.75, 0.6, 0.45]) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_THUMB_CHARS) return dataUrl;
  }
  throw new Error('That picture is too detailed to shrink. Try a smaller crop.');
}

/**
 * The picture on the clipboard, as a Blob — for "Paste picture": copy the ad's image in
 * Meta's Ad Library, then one click here. The browser asks permission the first time.
 */
export async function clipboardImage() {
  if (!navigator.clipboard || !navigator.clipboard.read) {
    throw new Error('This browser cannot read a picture from the clipboard. Use "Add picture" to choose a file.');
  }
  let items;
  try {
    items = await navigator.clipboard.read();
  } catch (e) {
    throw new Error('The clipboard could not be read. Allow it when the browser asks, or choose a file.');
  }
  for (const item of items) {
    const type = item.types.find((t) => /^image\/(jpeg|png|webp)$/.test(t));
    if (type) return item.getType(type);
  }
  throw new Error('There is no picture on the clipboard. Copy the ad\u2019s image first.');
}
